import type { TFunction } from 'i18next';
import type { SortCondition, SortOrder } from '@/shared/lib/sort/types';

/**
 * 事業（Project）のソート対象カラム ID 一覧（BE: `ProjectSortField` 準拠）。
 *
 * 歳出比較査定 (`GET /assessments/comparisons/:id`) と事業一覧 (`GET /projects`) の
 * 両 API で同じカラム集合を共有しているため、エンティティ層で共通定義する。
 * 順序は Select の選択肢表示順を兼ねる（BE 既定ソート順と同じ並び）。
 *
 * 注: BE 側は `project_name` のソートも受け付けるが、フロントの UI 選択肢からは
 * 意図的に除外している（事業一覧・歳出比較詳細のソート UI から事業名を非表示にする要件）。
 */
export const PROJECT_SORT_COLUMN_IDS = [
  'accounting_code',
  'kan_code',
  'ko_code',
  'moku_code',
  'project_code',
] as const;

export type ProjectSortColumnId = (typeof PROJECT_SORT_COLUMN_IDS)[number];

/**
 * 事業ソートの UI 既定条件（accounting → kan → ko → moku → project_code、全 asc）。
 * URL 未指定時の SortPopover プリセット表示、および「クリア」ボタンのフォールバックに使う。
 *
 * 注: BE 側は project_name を含む 6 フィールドのデフォルトを持つため「BE 既定」とは
 * 区別する。ここで定義するのはあくまで UI 上のプリセット。
 */
export const DEFAULT_PROJECT_SORT_CONDITIONS: ReadonlyArray<SortCondition<ProjectSortColumnId>> =
  PROJECT_SORT_COLUMN_IDS.map((id) => ({ columnId: id, order: 'asc' as const }));

/**
 * 並び替え条件が BE 既定（`DEFAULT_PROJECT_SORT_CONDITIONS`）と完全一致するかを判定する。
 * 配列の長さ・順序・各要素の columnId/order が全て同じであれば true。
 *
 * 適用時にこの判定で true なら URL から sort を削除して BE 既定に任せられる。
 */
export const isDefaultProjectSortConditions = (
  conditions: ReadonlyArray<SortCondition<ProjectSortColumnId>>,
): boolean => {
  if (conditions.length !== DEFAULT_PROJECT_SORT_CONDITIONS.length) return false;
  return conditions.every((c, i) => {
    const expected = DEFAULT_PROJECT_SORT_CONDITIONS[i];
    return expected !== undefined && c.columnId === expected.columnId && c.order === expected.order;
  });
};

/**
 * `ProjectSortColumnId` → 翻訳済みラベルの明示的マッピング
 * （i18n-safety: 動的キー生成（テンプレートリテラル）は禁止のため明示的に列挙する）
 *
 * ラベルは「〜コード」で統一する（"会計コード" / "款コード" など）。
 */
export const getProjectSortColumnLabel = ({
  columnId,
  t,
}: {
  columnId: ProjectSortColumnId;
  t: TFunction;
}): string => {
  const labelMap: Record<ProjectSortColumnId, string> = {
    accounting_code: t('common.fields.accounting-code'),
    kan_code: t('common.fields.kan-code'),
    ko_code: t('common.fields.ko-code'),
    moku_code: t('common.fields.moku-code'),
    project_code: t('project.fields.project-code'),
  };
  return labelMap[columnId];
};

export const isProjectSortColumnId = (value: string): value is ProjectSortColumnId =>
  (PROJECT_SORT_COLUMN_IDS as readonly string[]).includes(value);

const isSortOrder = (value: string): value is SortOrder => value === 'asc' || value === 'desc';

/**
 * 事業の `sort` クエリパラメータ（`<field>` または `<field>:<direction>` 形式の配列）を
 * SortCondition[] に変換する。
 *
 * 未知の field・不正な direction は寛容に除外し、URL 共有時に堅牢に動くようにする。
 * direction を省略した場合は BE と同じく `asc` 扱い。
 *
 * 事業一覧（`GET /projects?sort=...`）と財源詳細（クライアントソート）の両方で共有する。
 */
export const parseProjectSortConditions = (
  sort: readonly string[] | undefined,
): SortCondition<ProjectSortColumnId>[] => {
  if (!sort) return [];
  const result: SortCondition<ProjectSortColumnId>[] = [];
  for (const entry of sort) {
    if (typeof entry !== 'string' || entry.length === 0) continue;
    const colonIndex = entry.indexOf(':');
    const field = colonIndex === -1 ? entry : entry.slice(0, colonIndex);
    const directionRaw = colonIndex === -1 ? undefined : entry.slice(colonIndex + 1);
    if (!isProjectSortColumnId(field)) continue;
    if (directionRaw === undefined) {
      result.push({ columnId: field, order: 'asc' });
      continue;
    }
    if (!isSortOrder(directionRaw)) continue;
    result.push({ columnId: field, order: directionRaw });
  }
  return result;
};
